import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/**
 * Checks if the app is in maintenance mode.
 * Admins bypass maintenance mode so they can still access the app.
 */
export const useMaintenanceMode = (userId: string | undefined) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        // Check maintenance flag and admin status in parallel
        const [settingsResult, adminResult] = await Promise.all([
          supabase
            .from("app_settings" as any)
            .select("value")
            .eq("key", "maintenance_mode")
            .maybeSingle() as unknown as Promise<{ data: { value: any } | null; error: any }>,
          userId
            ? supabase.rpc("has_role", { _user_id: userId, _role: "admin" })
            : Promise.resolve({ data: false, error: null }),
        ]);

        if (cancelled) return;

        const maintenance = settingsResult.data?.value === true;
        const admin = !!adminResult.data;

        if (maintenance) {
          logger.log("[MaintenanceMode] Maintenance mode is ON, admin:", admin);
        }

        setIsMaintenanceMode(maintenance);
        setIsAdmin(admin);
      } catch (err) {
        logger.warn("[MaintenanceMode] Check failed, allowing access:", err);
      } finally {
        if (!cancelled) setChecked(true);
      }
    };

    void check();
    return () => { cancelled = true; };
  }, [userId]);

  // Admins bypass maintenance mode
  return {
    isMaintenanceMode: isMaintenanceMode && !isAdmin,
    checked,
  };
};
